import { lazy } from 'react';
import { importWithRetry, routeLoaders } from './routeLoaders';

/** EnsoMirae++ Autonomous OS — new screens */
export const OverviewPage = lazy(routeLoaders.overview);

export const IncidentsDomainPage = lazy(routeLoaders.incidents);

export const RuntimeLogsDomainPage = lazy(routeLoaders.runtimeLogs);

export const ReportedIncidentsPage = lazy(routeLoaders.reported);

// Eager — same failure mode as ProjectWorkspacePage below. A lazy Requests
// chunk can keep an older prebundled React while @mui/icons-material comes
// from a new one, so SvgIcon's useContext runs against a null dispatcher.
export { default as OperateRequestsPage } from '../components/projects/operate/OperateRequestsTab';

export const ExecutionsPage = lazy(routeLoaders.executions);

export const CertificationPage = lazy(routeLoaders.certification);

export const ExecutorCenterPage = lazy(routeLoaders.executors);

export const MonitoringPage = lazy(routeLoaders.monitoring);

export const ScheduledJobsPage = lazy(() =>
  import('../pages/monitoring/ScheduledJobsPage').then((m) => ({ default: m.default })),
);

export const PlatformInsightsPage = lazy(() =>
  import('../pages/PlatformInsightsPage').then((m) => ({ default: m.default })),
);

export const LegacyOperateRedirect = lazy(() =>
  import('../pages/LegacyOperateRedirect').then((m) => ({ default: m.default })),
);

export const ExecutiveBriefingPage = lazy(() =>
  import('../pages/ExecutiveBriefingPage').then((m) => ({ default: m.default })),
);

export const KnowledgePage = lazy(() =>
  import('../pages/KnowledgePage').then((m) => ({ default: m.default })),
);

/** Route-level code splitting — heavy views load on demand. */
export const ProjectsPage = lazy(routeLoaders.projects);

// Eager — this layout shell calls react-router hooks and wraps every lazy
// Operate/Phase outlet. Lazy-loading it has caused duplicate-React useContext
// crashes ("Cannot read properties of null (reading 'useContext')") after Vite
// dep re-optimizations (see vite.config.js header comment).
export { default as ProjectWorkspacePage } from '../components/projects/workspace/ProjectWorkspaceLayout';

export const ProjectHomePage = lazy(routeLoaders.projectHome);

// Eager — a few lines each (Suspense + Outlet). Lazy-loading them made every
// first Discover/Understand visit wait for the layout chunk before the tab
// chunk could even start downloading.
export { default as Phase0LayoutPage } from '../components/projects/workspace/Phase0Layout';

export const Phase0RepositoryPage = lazy(routeLoaders.phase0Repository);

export const Phase0ArtifactsPage = lazy(() =>
  import('../components/projects/phase0/Phase0ArtifactsTab').then((m) => ({ default: m.default })),
);

export const Phase0ChangesPage = lazy(() =>
  import('../components/projects/phase0/Phase0ChangesTab').then((m) => ({ default: m.default })),
);

export const Phase0ReviewPage = lazy(() =>
  import('../components/projects/phase0/Phase0ReviewTab').then((m) => ({ default: m.default })),
);

/** @deprecated legacy route aliases */
export const Phase0UploadPage = Phase0RepositoryPage;

export const Phase0RepoPage = lazy(() =>
  import('../components/projects/phase0/Phase0RepoTab').then((m) => ({ default: m.default })),
);

export const Phase0BaselinePage = lazy(() =>
  import('../components/projects/phase0/Phase0BaselineTab').then((m) => ({ default: m.default })),
);

export { default as Phase1LayoutPage } from '../components/projects/workspace/Phase1Layout';

export const Phase1InsightsPage = lazy(routeLoaders.phase1Insights);

export const Phase1GraphPage = lazy(routeLoaders.phase1Graph);

export const Phase1GraphExplorerPage = lazy(() =>
  importWithRetry(() =>
    import('../components/projects/phase0/ProjectGraphExplorer').then((m) => ({ default: m.default })),
  ),
);

export const Phase1GraphEnginePage = lazy(() =>
  importWithRetry(() =>
    import('../components/kg/KnowledgeGraphPage').then((m) => ({ default: m.default })),
  ),
);

export const Phase1KnowledgePage = lazy(routeLoaders.phase1Knowledge);

// Eager — same failure mode as ProjectWorkspacePage / Phase layouts. A lazy
// Operate shell that calls useLocation has crashed with duplicate-React
// useContext null after Vite dep re-optimizations; tab chunks stay lazy and
// are prefetched from OperateLayout on mount.
export { default as OperateLayoutPage } from '../components/projects/workspace/OperateLayout';

export const OperateFixesPage = lazy(routeLoaders.operateFixes);

export const OperateTestEnvironmentPage = lazy(routeLoaders.operateTestEnvironment);

export const OperateLearningPage = lazy(routeLoaders.operateLearning);

// P1 Milestone 1 — Governance
export const GovernanceLayoutPage = lazy(() =>
  import('../components/projects/governance-p1/GovernanceLayout').then((m) => ({ default: m.default })),
);

export const GovernancePoliciesPage = lazy(() =>
  import('../components/projects/governance-p1/GovernancePoliciesTab').then((m) => ({ default: m.default })),
);

export const GovernancePendingApprovalsPage = lazy(() =>
  import('../components/projects/governance-p1/GovernancePendingApprovalsTab').then((m) => ({ default: m.default })),
);

export const GovernanceCalendarPage = lazy(() =>
  import('../components/projects/governance-p1/GovernanceCalendarTab').then((m) => ({ default: m.default })),
);

export const GovernanceApprovalHistoryPage = lazy(() =>
  import('../components/projects/governance-p1/GovernanceApprovalHistoryTab').then((m) => ({ default: m.default })),
);

export const GovernanceTeamPage = lazy(() =>
  import('../components/projects/governance-p1/GovernanceTeamTab').then((m) => ({ default: m.default })),
);

// P1 M5-M8 top-level pages
export const ReleasesPage = lazy(() =>
  import('../components/projects/releases-p1/ReleasesTab').then((m) => ({ default: m.default })),
);
export const InsightsPage = lazy(() =>
  import('../pages/PlatformInsightsPage').then((m) => ({ default: m.default })),
);
export const PortfolioPage = lazy(() =>
  import('../components/projects/portfolio-p1/PortfolioTab').then((m) => ({ default: m.default })),
);
export const KnowledgeLibraryPage = lazy(() =>
  import('../components/projects/knowledge-p1/KnowledgeLibraryTab').then((m) => ({ default: m.default })),
);

// P2 — Enterprise Intelligence
export const EnterpriseLayoutPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterpriseLayout').then((m) => ({ default: m.default })),
);
export const EnterprisePortfolioPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterprisePortfolioTab').then((m) => ({ default: m.default })),
);
export const EnterpriseDependenciesPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterpriseDependenciesTab').then((m) => ({ default: m.default })),
);
export const EnterpriseBusinessImpactPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterpriseBusinessImpactTab').then((m) => ({ default: m.default })),
);
export const EnterprisePriorityPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterprisePriorityTab').then((m) => ({ default: m.default })),
);
export const EnterpriseExecutivePage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterpriseExecutiveTab').then((m) => ({ default: m.default })),
);
export const EnterpriseStrategyPage = lazy(() =>
  import('../components/projects/enterprise-p2/EnterpriseStrategyTab').then((m) => ({ default: m.default })),
);

export const SettingsPage = lazy(routeLoaders.settings);

export const Phase0ReportPage = lazy(() =>
  import('../components/reporting/Phase0ReportView').then((m) => ({ default: m.default })),
);

export const Phase0HandoffPage = lazy(() =>
  import('../components/handoff/Phase0HandoffView').then((m) => ({ default: m.default })),
);

export const ProjectIngestionPage = lazy(() =>
  import('../components/projects/ProjectIngestionRedirect').then((m) => ({ default: m.default })),
);

export const ProjectLegacyRedirect = lazy(() =>
  import('../components/projects/ProjectLegacyRedirect').then((m) => ({ default: m.default })),
);
